<template>
    <div v-if="props.showFilters && props.countryId === 'Gb'">
        <div v-for="category, categoryKey in plantCategories" :key="categoryKey">
            <input
                v-if="props.filterCategoriesToShow.includes(categoryKey)"
                v-model="categoryToShow[categoryKey]"
                type="checkbox"
                :id="`category-${categoryKey}`" />
            <label :for="`category-${categoryKey}`">{{ category.categoryName }}</label>
        </div>
    </div>
    <hover-tooltip v-if="props.disabled" :tooltipText="props.disabledText" />
    <spark-multi-select
        ref="selectRef"
        :model-value="localSelectedPlants"
        :options="filteredPlants"
        option-label="id"
        summary-label="plant"
        :filter-fields="['id', 'name']"
        :placeholder="props.placeholderOverride"
        :reset-filter-on-hide="props.resetAfter"
        @update:model-value="updateSelectedPlants"
        @show="onShow"
        @hide="onHide"
        scroll-height="300px"
        :selection-limit="props.multiSelectLimit"
        :class="['plant-selector', { 'full-width': props.fullWidth }]"
        :virtual-scroller-options="{ itemSize: 70 }"
        v-if="props.multiSelect"
    >
        <template #optiongroup="slotProps">
            <div>
                <div>{{ slotProps.option.label }}</div>
            </div>
        </template>
        <template #option="slotProps">
            <div class="option-row">
                <div class="option-row-text">
                    <div class="id-text">
                        {{ slotProps.option.id }}
                    </div>
                    <div class="sub-text">
                        {{ slotProps.option.name }}
                    </div>
                    <div class="sub-text">
                        {{ slotProps.option.owner}}
                    </div>
                </div>
                <icofont-icon
                    background-colour="transparent"
                    :icon-colour="slotProps.option.colour"
                    :icon="slotProps.option.icon"
                    size="large"
                />
            </div>
        </template>
    </spark-multi-select>
    <primevue-select
        ref="selectRef"
        :model-value="selectedPlants[0]"
        :input-id="props.inputId"
        :aria-label="props.ariaLabel"
        auto-filter-focus
        filter
        :filter-fields="['id', 'name']"
        option-label="id"
        :options="filteredPlants"
        :placeholder="props.placeholderOverride"
        :reset-filter-on-hide="props.resetAfter"
        scroll-height="300px"
        :class="['plant-selector', { 'full-width': props.fullWidth }]"
        :virtual-scroller-options="{ itemSize: 60 }"
        @update:model-value="emitValue"
        @show="onShow"
        @hide="onHide"
        v-else
    >
        <template #dropdownicon>
            <font-awesome-icon class="dropdown-icon" :class="{ inverted: isOpen }" :icon="['fas', 'caret-down']" />
        </template>
        <template #option="slotProps">
            <div class="option-row">
                <div class="option-row-text">
                    <div class="id-text">
                        {{ slotProps.option.id }}
                    </div>
                    <div class="sub-text">
                        {{ slotProps.option.name }}
                    </div>
                    <div class="sub-text">
                        {{ slotProps.option.owner}}
                    </div>
                </div>
                <icofont-icon
                    background-colour="transparent"
                    :icon-colour="slotProps.option.colour"
                    :icon="slotProps.option.icon"
                    size="large"
                />
            </div>
        </template>
    </primevue-select>
</template>

<script lang="ts" setup>
import {
    computed, ref, watch, useTemplateRef,
    type ComponentPublicInstance,
} from 'vue';
import PrimevueSelect from 'primevue/select';
import { IPlant } from '@/interfaces/powerMarkets/plant/IPlant.ts';
import { useDataStore } from '@/stores/powerMarkets/dataStore.ts';
import { useUserSettingsStore } from '@/stores/userSettingsStore.ts';
import { IPlantCategoriesByType } from '@/interfaces/powerMarkets/plant/IPlantCategoriesByType.ts';
import HoverTooltip from '@/components/powerMarkets/common/HoverTooltip.vue';
import SparkMultiSelect from '@/components/common/SparkMultiSelect.vue';
import IcofontIcon from '../common/IcofontIcon.vue';

const dataStore = useDataStore();
const userSettingsStore = useUserSettingsStore();

const props = withDefaults(
    defineProps<{
        selectedPlantIds?: Array<string> | string | null;
        showFilters?: boolean;
        filterCategoriesToShow?: Array<string>;
        placeholderOverride?: string;
        fullWidth?: boolean;
        customPlantsToDisplay?: Array<string>;
        customPlantsToHide?: Array<string>;
        resetAfter?: boolean;
        multiSelect?: boolean;
        multiSelectLimit?: number;
        countryId?: string;
        aggregateType?: string;
        disabled?: boolean;
        disabledText?: string;
        invalid?: boolean;
        id?: string;
        inputId?: string;
        ariaLabel?: string;
        plantOptionsOverride?: Array<string>;
    }>(),
    {
        filterCategoriesToShow: () => ['BmPlant', 'NonBmPlant'],
        placeholderOverride: 'Search for a plant',
        customPlantsToDisplay: () => [],
        customPlantsToHide: () => [],
        countryId: 'Gb',
        aggregateType: 'plant',
        id: undefined,
        inputId: undefined,
        ariaLabel: undefined,
        disabledText: '',
        plantOptionsOverride: undefined,
        selectedPlantIds: null,
        multiSelectLimit: undefined,
    },
);

const emit = defineEmits<{
    'update:model-value-single': [IPlant];
    'update:model-value-multiple': [Array<IPlant>];
}>();

const categoryToShow = ref<Record<string, boolean>>({});
const isOpen = ref<boolean>(false);
const selectRef = useTemplateRef<ComponentPublicInstance>('selectRef');

const plantCategories = computed<IPlantCategoriesByType | null>(() => dataStore.plantStore?.allPlants ?? null);
const chosenPlantsByGroup = computed<Record<string, Array<string>> | null>(() => userSettingsStore.userSettings.otherSettings.data.chosenPlantsByGroup ?? null);

const allPlants = computed<Array<IPlant>>(() => {
    let listToFilter: Array<IPlant> = [];

    if (props.countryId === 'Gb') {
        if (props.aggregateType === 'group' && chosenPlantsByGroup.value) {
            const plantsByGroup = chosenPlantsByGroup.value;
            Object.keys(plantsByGroup).forEach(groupName => {
                listToFilter.push({
                    name: groupName,
                    searchable: true,
                    id: groupName,
                    plantsForGroup: plantsByGroup[groupName],
                    icon: 'group',
                    colour: 'rgb(233, 63, 111)',
                });
            });
        } else {
            const categories = plantCategories.value;
            if (!categories) return [];
            Object.keys(categories).forEach(category => {
                if (props.filterCategoriesToShow.includes(category)) listToFilter.push(...categories[category as keyof IPlantCategoriesByType].plants);
            });
        }
    } else {
        const plantsByEndpoint = dataStore.plantsByEndpoint;
        Object.keys(plantsByEndpoint).forEach(endpoint => {
            const plantsByCountry = plantsByEndpoint[endpoint].plantList;
            Object.keys(plantsByCountry).filter(c => c === props.countryId).forEach(country => {
                listToFilter = plantsByCountry[country];
            });
        });
    }

    if (props.customPlantsToDisplay.length) {
        listToFilter = listToFilter.filter(plant => props.customPlantsToDisplay.includes(plant.id));
    } else if (props.customPlantsToHide.length) {
        listToFilter = listToFilter.filter(plant => !props.customPlantsToHide.includes(plant.id));
    }

    return listToFilter.filter(plant => plant.searchable);
});

const filteredPlants = computed<Array<IPlant>>(() => {
    const plantOptions = props.plantOptionsOverride;
    if (plantOptions?.length) return allPlants.value.filter(plant => plantOptions.includes(plant.id));
    return allPlants.value;
});

const selectedPlants = computed<Array<IPlant>>(() => {
    if (!props.selectedPlantIds) return [];
    if (Array.isArray(props.selectedPlantIds)) {
        return allPlants.value.filter(plant => props.selectedPlantIds?.includes(plant.id) ?? false);
    }
    return allPlants.value.filter(plant => props.selectedPlantIds === plant.id);
});

function updateSelectedPlants (plants: Array<IPlant>): void {
    localSelectedPlants.value = plants;
    emitValue(plants);
}

const localSelectedPlants = ref<Array<IPlant>>(selectedPlants.value);

function emitValue (value: Array<IPlant> | IPlant): void {
    if (Array.isArray(value)) {
        emit('update:model-value-multiple', value);
        return;
    }
    emit('update:model-value-single', value);
}

function onShow (): void {
    isOpen.value = true;
}

function onHide (): void {
    isOpen.value = false;
}

function focus (): void {
    const rootElement = selectRef.value?.$el;
    if (!rootElement) return;

    const focusTarget = rootElement.matches('[role="combobox"], input, [tabindex]')
        ? rootElement
        : rootElement.querySelector('[role="combobox"], input, [tabindex], .p-multiselect, .p-select');

    focusTarget?.focus();
}

defineExpose({
    focus,
});

watch(() => props.selectedPlantIds, () => {
    localSelectedPlants.value = selectedPlants.value;
});
</script>

<style lang="scss" scoped>
.plant-selector {
    width: 350px;
    @include responsive('sm') {
        width: 100%;
    }
    &.full-width {
        width: 100%;
    }
}

.dropdown-icon {
    font-size: var(--smallFontSize);

    &.inverted {
        transform: rotate(180deg);
    }
}

.option-row {
    display: flex;
    gap: var(--defaultSpacing);
    align-items: center;
    width: 100%;

    .option-row-text {
        margin-right: auto;
        margin-left: var(--smallSpacing);

        .id-text {
            font-size: var(--smallFontSize);
            font-weight: var(--boldFontWeight);
        }

        .sub-text {
            font-size: var(--extraSmallFontSize);
            overflow-wrap: break-word;
            white-space: wrap;
        }
    }
}
</style>
